import React, { useState, useEffect } from 'react';
import { withTranslation } from "react-i18next";
import { Card, Button, Form, Alert, Row, Col, Breadcrumb, Tabs, Tab } from 'react-bootstrap';
import './DataSourceConfiguration.scss';
import { BiPlus } from 'react-icons/bi';

const DataSourceConfiguration = ({ t }) => {
    const [activeTab, setActiveTab] = useState('api');
    const [connectionStatus, setConnectionStatus] = useState({
        status: 'connected',
        lastSync: '2 hours ago',
        nextSync: 'Jan 15, 2025 09:00',
        dataPoints: 6
    });
    const [connectionSettings, setConnectionSettings] = useState({
        baseUrl: 'https://api.company.com/v2',
        timeout: 30,
        apiKey: '••••••••••••••••',
        rateLimit: 100
    });
    const [syncSettings, setSyncSettings] = useState({
        frequency: 'Quarterly',
        retryAttempts: 3,
        retryDelay: 60,
        dataValidation: 'Strict'
    });
    const [dataPoints, setDataPoints] = useState([
        { name: 'Radioactive waste', frequency: 'Quarterly', status: 'connected' },
        { name: 'Battery waste', frequency: 'Quarterly', status: 'connected' },
        { name: 'Construction waste', frequency: 'Quarterly', status: 'connected' },
        { name: 'Bio-medical waste', frequency: 'Quarterly', status: 'connected' },
        { name: 'E-waste', frequency: 'Quarterly', status: 'connected' },
        { name: 'Plastic waste', frequency: 'Quarterly', status: 'connected' }
    ]);

    const dataSources = [
        {
            id: 'api',
            name: 'API Internal API',
            icon: 'API',
            iconColor: 'purple',
            status: 'connected',
            statusColor: 'success'
        },
        {
            id: 'df',
            name: 'DF Digital Forms',
            icon: 'DF',
            iconColor: 'orange',
            status: 'warning',
            statusColor: 'warning'
        },
        {
            id: 'sap',
            name: 'SAP SAP ERP',
            icon: 'SAP',
            iconColor: 'light-blue',
            status: 'error',
            statusColor: 'danger'
        }
    ];

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
    };

    const handleTestConnection = () => {
        console.log('Testing connection...');
    };

    const handleViewLogs = () => {
        console.log('Viewing logs...');
    };

    const handleSaveChanges = () => {
        console.log('Saving changes...');
    };

    const handleInputChange = (section, field, value) => {
        if (section === 'connection') {
            setConnectionSettings(prev => ({
                ...prev,
                [field]: value
            }));
        } else if (section === 'sync') {
            setSyncSettings(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'connected':
                return <span className="status-dot status-dot-success"></span>;
            case 'warning':
                return <span className="status-dot status-dot-warning"></span>;
            case 'error':
                return <span className="status-dot status-dot-danger"></span>;
            default:
                return <span className="status-dot status-dot-secondary"></span>;
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'connected':
                return t("DataSourceConfiguration.status.connected.label");
            case 'warning':
                return t("DataSourceConfiguration.status.warning.label");
            case 'error':
                return t("DataSourceConfiguration.status.error.label");
            default:
                return t("DataSourceConfiguration.status.unknown.label");
        }
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'connected':
                return 'text-success';
            case 'warning':
                return 'text-warning';
            case 'error':
                return 'text-danger';
            default:
                return 'text-secondary';
        }
    };

    const getCurrentDataSource = () => {
        return dataSources.find(source => source.id === activeTab) || dataSources[0];
    };

    return (
        <div className="DS-configuration" data-testid="data-source-configuration">

            <div>
                <div className="List_header pb-2">
                    <Breadcrumb className="h5 m-0">
                        <Breadcrumb.Item href="#">{t("Configurations.label")}</Breadcrumb.Item>
                        <Breadcrumb.Item active>{t("DataSourceConfiguration.title.label")}</Breadcrumb.Item>
                    </Breadcrumb>
                </div>
                <p className="text-muted">{t("DataSourceConfiguration.description.label")}</p>
            </div>
            <Card className="DS-tabs-card mb-4">
                <Tabs
                    defaultActiveKey="InternalAPI"
                    id="uncontrolled-tab-example"
                    className="mb-0"
                >
                    <Tab eventKey="InternalAPI" title={
                        <div className='tab-button'>
                            <div className="tab-icon api-icon">API</div>
                            <span className="source-title">Internal API</span>
                            <div className="status-indicator status-connected"></div>
                        </div>
                    }
                    >
                        <div id="api" >
                            <div class="source-header">
                                <div class="source-info">
                                    <div class="source-icon-large api-icon">API</div>
                                    <div>
                                        <div class="source-title">Internal API</div>
                                        <div class="source-subtitle">REST API v2.1 - ESG Data Collection</div>
                                    </div>
                                </div>
                                <div class="source-actions">
                                    <button class="btn btn-primary">Test Connection</button>
                                    <button class="btn btn-secondary">View Logs</button>
                                    <button class="btn btn-secondary">Save Changes</button>
                                </div>
                            </div>

                            <div class="alert alert-info">
                                <div>✓</div>
                                <div>Connection successful. Last sync: 2 hours ago</div>
                            </div>

                            <div class="status-section">
                                <div class="section-title">
                                    <div class="section-icon"></div>
                                    Connection Status
                                </div>
                                <div class="status-grid">
                                    <div class="status-item">
                                        <div class="status-label">Status</div>
                                        <div class="status-value status-connected">Connected</div>
                                    </div>
                                    <div class="status-item">
                                        <div class="status-label">Last Sync</div>
                                        <div class="status-value">2 hours ago</div>
                                    </div>
                                    <div class="status-item">
                                        <div class="status-label">Next Sync</div>
                                        <div class="status-value">Jan 15, 2025 09:00</div>
                                    </div>
                                    <div class="status-item">
                                        <div class="status-label">Data Points</div>
                                        <div class="status-value">6 connected</div>
                                    </div>
                                </div>
                            </div>

                            <div class="config-sections">
                                <div class="config-section">
                                    <div class="section-title">
                                        <div class="section-icon"></div>
                                        Connection Settings
                                    </div>
                                    <div class="config-grid">
                                        <div class="config-item">
                                            <div class="config-label">Base URL</div>
                                            <input type="text" class="form-control" value="https://api.company.com/v2" />
                                        </div>
                                        <div class="config-item">
                                            <div class="config-label">Timeout (seconds)</div>
                                            <input type="number" class="form-control" value="30" />
                                        </div>
                                        <div class="config-item">
                                            <div class="config-label">API Key</div>
                                            <input type="password" class="form-control" value="••••••••••••••••" />
                                        </div>
                                        <div class="config-item">
                                            <div class="config-label">Rate Limit (req/min)</div>
                                            <input type="number" class="form-control" value="100" />
                                        </div>
                                    </div>
                                </div>

                                <div class="config-section">
                                    <div class="section-title">
                                        <div class="section-icon"></div>
                                        Synchronization
                                    </div>
                                    <div class="config-grid">
                                        <div class="config-item">
                                            <div class="config-label">Sync Frequency</div>
                                            <select class="form-select">
                                                <option>Quarterly</option>
                                                <option>Monthly</option>
                                                <option>Weekly</option>
                                                <option>Daily</option>
                                            </select>
                                        </div>
                                        <div class="config-item">
                                            <div class="config-label">Retry Attempts</div>
                                            <input type="number" class="form-control" value="3" />
                                        </div>
                                        <div class="config-item">
                                            <div class="config-label">Retry Delay (seconds)</div>
                                            <input type="number" class="form-control" value="60" />
                                        </div>
                                        <div class="config-item">
                                            <div class="config-label">Data Validation</div>
                                            <select class="form-select">
                                                <option>Strict</option>
                                                <option>Standard</option>
                                                <option>Lenient</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="data-points-section">
                                <div class="data-points-title">Connected Data Points (6)</div>
                                <div class="data-points-grid">
                                    <div class="data-point-card">
                                        <div class="data-point-info">
                                            <div class="data-point-name">Radioactive waste</div>
                                            <div class="data-point-frequency">Quarterly</div>
                                        </div>
                                        <div class="data-point-status status-connected"></div>
                                    </div>
                                    <div class="data-point-card">
                                        <div class="data-point-info">
                                            <div class="data-point-name">Battery waste</div>
                                            <div class="data-point-frequency">Quarterly</div>
                                        </div>
                                        <div class="data-point-status status-connected"></div>
                                    </div>
                                    <div class="data-point-card">
                                        <div class="data-point-info">
                                            <div class="data-point-name">Construction waste</div>
                                            <div class="data-point-frequency">Quarterly</div>
                                        </div>
                                        <div class="data-point-status status-connected"></div>
                                    </div>
                                    <div class="data-point-card">
                                        <div class="data-point-info">
                                            <div class="data-point-name">Bio-medical waste</div>
                                            <div class="data-point-frequency">Quarterly</div>
                                        </div>
                                        <div class="data-point-status status-connected"></div>
                                    </div>
                                    <div class="data-point-card">
                                        <div class="data-point-info">
                                            <div class="data-point-name">E-waste</div>
                                            <div class="data-point-frequency">Quarterly</div>
                                        </div>
                                        <div class="data-point-status status-connected"></div>
                                    </div>
                                    <div class="data-point-card">
                                        <div class="data-point-info">
                                            <div class="data-point-name">Plastic waste</div>
                                            <div class="data-point-frequency">Quarterly</div>
                                        </div>
                                        <div class="data-point-status status-connected"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Tab>
                    <Tab eventKey="DigitalForms" title={
                        <div className='tab-button'>
                            <div className="tab-icon df-icon">DF</div>
                            <span className="source-title">Digital Forms</span>
                            <div className="status-indicator status-warning"></div>
                        </div>
                    }>
                        <div id="forms" >
                            <div class="source-header">
                                <div class="source-info">
                                    <div class="source-icon-large df-icon">DF</div>
                                    <div>
                                        <div class="source-title">Digital Forms</div>
                                        <div class="source-subtitle">Employee Data Collection Platform</div>
                                    </div>
                                </div>
                                <div class="source-actions">
                                    <button class="btn btn-primary">Test Connection</button>
                                    <button class="btn btn-secondary">Configure Forms</button>
                                    <button class="btn btn-secondary">Save Changes</button>
                                </div>
                            </div>

                            <div class="alert alert-warning">
                                <div>⚠</div>
                                <div>Synchronization in progress. Next update in 15 minutes.</div>
                            </div>

                            <div class="status-section">
                                <div class="section-title">
                                    <div class="section-icon"></div>
                                    Connection Status
                                </div>
                                <div class="status-grid">
                                    <div class="status-item">
                                        <div class="status-label">Status</div>
                                        <div class="status-value status-warning">Syncing</div>
                                    </div>
                                    <div class="status-item">
                                        <div class="status-label">Last Sync</div>
                                        <div class="status-value">45 minutes ago</div>
                                    </div>
                                    <div class="status-item">
                                        <div class="status-label">Response Rate</div>
                                        <div class="status-value">87%</div>
                                    </div>
                                    <div class="status-item">
                                        <div class="status-label">Data Points</div>
                                        <div class="status-value">3 connected</div>
                                    </div>
                                </div>
                            </div>

                            <div class="config-sections">
                                <div class="config-section">
                                    <div class="section-title">
                                        <div class="section-icon"></div>
                                        Authentication
                                    </div>
                                    <div class="config-grid">
                                        <div class="config-item">
                                            <div class="config-label">Service Account</div>
                                            <input type="text" class="form-control" value="hr-forms@company.com" />
                                        </div>
                                        <div class="config-item">
                                            <div class="config-label">OAuth Token</div>
                                            <input type="password" class="form-control" value="••••••••••••••••" />
                                        </div>
                                        <div class="config-item full-width">
                                            <div class="config-label">Form Collection URL</div>
                                            <input type="text" class="form-control" value="https://forms.company.com/api/responses" />
                                        </div>
                                    </div>
                                </div>

                                <div class="config-section">
                                    <div class="section-title">
                                        <div class="section-icon"></div>
                                        Data Processing
                                    </div>
                                    <div class="config-grid">
                                        <div class="config-item">
                                            <div class="config-label">Validation Rules</div>
                                            <select class="form-select">
                                                <option>Standard HR Rules</option>
                                                <option>Custom Validation</option>
                                                <option>No Validation</option>
                                            </select>
                                        </div>
                                        <div class="config-item">
                                            <div class="config-label">Data Retention</div>
                                            <input type="text" class="form-control" value="2 years" />
                                        </div>
                                        <div class="config-item">
                                            <div class="config-label">Anonymization</div>
                                            <select class="form-select">
                                                <option>Full Anonymization</option>
                                                <option>Partial Anonymization</option>
                                                <option>No Anonymization</option>
                                            </select>
                                        </div>
                                        <div class="config-item">
                                            <div class="config-label">Sync Frequency</div>
                                            <select class="form-select">
                                                <option>Half Yearly</option>
                                                <option>Quarterly</option>
                                                <option>Monthly</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="data-points-section">
                                <div class="data-points-title">Connected Data Points (3)</div>
                                <div class="data-points-grid">
                                    <div class="data-point-card">
                                        <div class="data-point-info">
                                            <div class="data-point-name">Employees more than 50 years old</div>
                                            <div class="data-point-frequency">Half Yearly</div>
                                        </div>
                                        <div class="data-point-status status-warning"></div>
                                    </div>
                                    <div class="data-point-card">
                                        <div class="data-point-info">
                                            <div class="data-point-name">Employees between 30 and 50 years old</div>
                                            <div class="data-point-frequency">Half Yearly</div>
                                        </div>
                                        <div class="data-point-status status-warning"></div>
                                    </div>
                                    <div class="data-point-card">
                                        <div class="data-point-info">
                                            <div class="data-point-name">Employees younger than 30 years old</div>
                                            <div class="data-point-frequency">Half Yearly</div>
                                        </div>
                                        <div class="data-point-status status-warning"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Tab>
                    <Tab eventKey="SAPERP" title={
                        <div className='tab-button'>
                            <div className="tab-icon sap-icon">SAP</div>
                            <span className="source-title">SAP ERP</span>
                            <div className="status-indicator status-error"></div>
                        </div>
                    }>
                        <div id="sap" >
                            <div class="source-header">
                                <div class="source-info">
                                    <div class="source-icon-large sap-icon">SAP</div>
                                    <div>
                                        <div class="source-title">SAP ERP</div>
                                        <div class="source-subtitle">Operations & Energy Data</div>
                                    </div>
                                </div>
                                <div class="source-actions">
                                    <button class="btn btn-primary">Test Connection</button>
                                    <button class="btn btn-secondary">Debug Connection</button>
                                    <button class="btn btn-secondary">Save Changes</button>
                                </div>
                            </div>

                            <div class="alert alert-danger ">
                                <div>✕</div>
                                <div>Connection failed: Timeout error. Last attempt 15 minutes ago.</div>
                            </div>

                            <div class="status-section">
                                <div class="section-title">
                                    <div class="section-icon"></div>
                                    Connection Status
                                </div>
                                <div class="status-grid">
                                    <div class="status-item">
                                        <div class="status-label">Status</div>
                                        <div class="status-value status-error">Connection Error</div>
                                    </div>
                                    <div class="status-item">
                                        <div class="status-label">Last Successful Sync</div>
                                        <div class="status-value">2 days ago</div>
                                    </div>
                                    <div class="status-item">
                                        <div class="status-label">Error Count</div>
                                        <div class="status-value status-error">12 (last 24h)</div>
                                    </div>
                                    <div class="status-item">
                                        <div class="status-label">Data Points</div>
                                        <div class="status-value">1 configured</div>
                                    </div>
                                </div>
                            </div>

                            <div class="config-sections">
                                <div class="config-section">
                                    <div class="section-title">
                                        <div class="section-icon"></div>
                                        SAP Connection
                                    </div>
                                    <div class="config-grid">
                                        <div class="config-item">
                                            <div class="config-label">System ID</div>
                                            <input type="text" class="form-control" value="PRD" />
                                        </div>
                                        <div class="config-item">
                                            <div class="config-label">Client</div>
                                            <input type="text" class="form-control" value="100" />
                                        </div>
                                        <div class="config-item">
                                            <div class="config-label">Application Server</div>
                                            <input type="text" class="form-control" value="sap.company.local" />
                                        </div>
                                        <div class="config-item">
                                            <div class="config-label">System Number</div>
                                            <input type="text" class="form-control" value="00" />
                                        </div>
                                        <div class="config-item">
                                            <div class="config-label">Username</div>
                                            <input type="text" class="form-control" value="ESG_USER" />
                                        </div>
                                        <div class="config-item">
                                            <div class="config-label">Password</div>
                                            <input type="password" class="form-control" value="••••••••••••••••" />
                                        </div>
                                    </div>
                                </div>

                                <div class="config-section">
                                    <div class="section-title">
                                        <div class="section-icon"></div>
                                        Query Configuration
                                    </div>
                                    <div class="config-grid">
                                        <div class="config-item full-width">
                                            <div class="config-label">Custom Query/RFC Function</div>
                                            <input type="text" class="form-control" value="Z_ESG_ELECTRICITY_DATA" />
                                        </div>
                                        <div class="config-item">
                                            <div class="config-label">Connection Timeout</div>
                                            <input type="number" class="form-control" value="60" />
                                        </div>
                                        <div class="config-item">
                                            <div class="config-label">Query Timeout</div>
                                            <input type="number" class="form-control" value="300" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="data-points-section">
                                <div class="data-points-title">Connected Data Points (1)</div>
                                <div class="data-points-grid">
                                    <div class="data-point-card">
                                        <div class="data-point-info">
                                            <div class="data-point-name">Electricity consumption from Nuclear</div>
                                            <div class="data-point-frequency">Monthly</div>
                                        </div>
                                        <div class="data-point-status status-error"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Tab>
                </Tabs>
            </Card>
        </div>
    );
};

export default withTranslation()(DataSourceConfiguration);